import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Device } from "../../../../../api/devices";
import type { UserListItemOut } from "../../../../../api/auth";
import { useViewport } from "../../../../../hooks/useViewport";
import { useUnsavedChangesGuard } from "../../panel/useUnsavedChangesGuard";
import { deviceFormSnapshot } from "../deviceFormSnapshot";
import type { DeviceFormValues } from "../DeviceFormModal";
import { buildDeviceAccounts } from "../modal/deviceAccounts";
import DeviceModalShell, { DeviceSummaryItem } from "../modal/DeviceModalShell";
import { TerminalIcon } from "../modal/deviceModalIcons";
import TerminalAccountsSection from "./TerminalAccountsSection";

interface Props {
  open: boolean;
  // Създаване (device=null) или редакция.
  device: Device | null;
  users: UserListItemOut[];
  devices: Device[];
  // Новият GUID при създаване идва от извикващия (общ с другите типове).
  newGuid: () => string;
  // Извикващият прави мутацията и toast-овете; при грешка модалът остава отворен.
  onSubmit: (values: DeviceFormValues) => Promise<void>;
  onCancel: () => void;
}

/**
 * Модалът на терминал за плащане (създаване и редакция) — името и една
 * секция с акаунтите. Логиката е тази на досегашната обща форма: статус в
 * интерфейса няма, но заварен терминал без акаунти тръгва „изключен" и избор
 * на акаунт го включва; изключен праща празен списък.
 */
const TerminalModal = ({ open, device, users, devices, newGuid, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const isEdit = device !== null;

  const accounts = useMemo(
    () => buildDeviceAccounts(users, devices, "paymentTerminal", device?.id ?? null),
    [users, devices, device],
  );

  // Заварено устройство без акаунти тръгва изключено (както досега).
  const initiallyEnabled = !isEdit || device.userIds.length > 0;

  const [name, setName] = useState(device?.name ?? "");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(device?.userIds ?? []);
  const [isEnabled, setIsEnabled] = useState(initiallyEnabled);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { requestClose, markPristine, unsavedDialog } = useUnsavedChangesGuard({
    open,
    snapshot: deviceFormSnapshot({
      name,
      userIds: selectedUserIds,
      stationIds: [],
      printLanguage: null,
      paymentMethods: [],
      summarizeReceipt: false,
      nonFiscalCopy: false,
      isEnabled,
    }),
    isSubmitting,
    onClose: onCancel,
  });

  useEffect(() => {
    if (!open) return;
    setName(device?.name ?? "");
    setSelectedUserIds(device?.userIds ?? []);
    setIsEnabled(initiallyEnabled);
    markPristine();
  }, [open, device, initiallyEnabled, markPristine]);

  // Избор на акаунт включва устройството. Стабилна референция — секцията е memo.
  const handleAccountsChange = useCallback((ids: number[]) => {
    setSelectedUserIds(ids);
    if (ids.length > 0) setIsEnabled(true);
  }, []);

  // Създаването изисква акаунт; редакцията може да „изпразни" устройството.
  const hasAssignment = selectedUserIds.length > 0;
  const canSave = name.trim().length > 0 && !isSubmitting && (isEdit || hasAssignment);

  const handleSave = async () => {
    if (!canSave) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        guid: device?.guid ?? newGuid(),
        name: name.trim(),
        type: "paymentTerminal",
        userIds: isEnabled ? selectedUserIds : [],
        stationIds: [],
        printLanguage: null,
        summarizeReceipt: false,
        nonFiscalCopy: false,
        paymentMethods: [],
      });
    } catch {
      // Toast-ът за грешка е при извикващия; модалът остава отворен.
    } finally {
      setIsSubmitting(false);
    }
  };

  const chosenCount = accounts.filter(
    (account) => account.takenBy === null && selectedUserIds.includes(account.id),
  ).length;

  return (
    <>
      <DeviceModalShell
        open={open}
        onRequestClose={requestClose}
        icon={<TerminalIcon size={20} />}
        kicker={isEdit ? t("devices.terminalModal.kickerEdit") : t("devices.terminalModal.kickerCreate")}
        phoneKicker={t("devices.typePaymentTerminal")}
        name={name}
        onNameChange={setName}
        autoFocusName={!isEdit}
        summary={
          <DeviceSummaryItem
            dotClassName="bg-brand"
            label={t("devices.terminalModal.summaryAccounts")}
            value={t("devices.terminalModal.summaryCount", { count: chosenCount })}
          />
        }
        saveLabel={isEdit ? t("devices.saveChanges") : t("devices.addDevice")}
        canSave={canSave}
        onSave={handleSave}
        size="compact"
        gridClassName="grid-cols-1"
      >
        <TerminalAccountsSection
          accounts={accounts}
          selectedIds={selectedUserIds}
          onSelectedChange={handleAccountsChange}
          isNarrow={isNarrow}
        />
      </DeviceModalShell>

      {unsavedDialog}
    </>
  );
};

export default TerminalModal;
